<template>
    <div
        class="rounded-lg border border-primary-200 bg-white p-5 shadow dark:border-primary-800 dark:bg-gray-800"
    >
        <div class="mb-3 flex items-center justify-between gap-3">
            <h3
                class="inline-flex items-center text-sm font-semibold text-gray-900 dark:text-white"
            >
                <Sparkles class="mr-2 h-5 w-5 text-primary-500" />
                {{ t("dashboard.aiInsight.title") }}
            </h3>
            <button
                v-if="insight === null"
                type="button"
                class="inline-flex items-center rounded-md border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20"
                :disabled="loading"
                @click="generate"
            >
                <Loader2
                    v-if="loading"
                    class="mr-1.5 h-3.5 w-3.5 animate-spin"
                />
                {{
                    loading
                        ? t("dashboard.aiInsight.loading")
                        : t("dashboard.aiInsight.generate")
                }}
            </button>
            <button
                v-else
                type="button"
                class="text-xs text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                :title="t('common.close')"
                @click="insight = null"
            >
                <X class="h-4 w-4" />
            </button>
        </div>

        <div v-if="error" class="text-sm text-danger-600 dark:text-danger-400">
            {{ error }}
            <button type="button" class="ml-2 underline" @click="generate">
                {{ t("common.retry") }}
            </button>
        </div>

        <div v-else-if="insight" class="space-y-3">
            <p class="text-sm text-gray-700 dark:text-gray-300">
                {{ insight.summary }}
            </p>

            <div
                v-if="insight.highlights?.length"
                class="rounded-md bg-success-50 p-3 dark:bg-success-900/20"
            >
                <p
                    class="mb-1 text-xs font-semibold text-success-700 dark:text-success-300"
                >
                    {{ t("dashboard.aiInsight.highlights") }}
                </p>
                <ul
                    class="list-disc space-y-0.5 pl-4 text-xs text-success-700 dark:text-success-300"
                >
                    <li
                        v-for="(item, index) in insight.highlights"
                        :key="index"
                    >
                        {{ item }}
                    </li>
                </ul>
            </div>

            <div
                v-if="insight.risks?.length"
                class="rounded-md bg-warning-50 p-3 dark:bg-warning-900/20"
            >
                <p
                    class="mb-1 text-xs font-semibold text-warning-700 dark:text-warning-300"
                >
                    {{ t("dashboard.aiInsight.risks") }}
                </p>
                <ul
                    class="list-disc space-y-0.5 pl-4 text-xs text-warning-700 dark:text-warning-300"
                >
                    <li v-for="(item, index) in insight.risks" :key="index">
                        {{ item }}
                    </li>
                </ul>
            </div>

            <div
                v-if="insight.recommendations?.length"
                class="rounded-md bg-primary-50 p-3 dark:bg-primary-900/20"
            >
                <p
                    class="mb-1 text-xs font-semibold text-primary-700 dark:text-primary-300"
                >
                    {{ t("dashboard.aiInsight.recommendations") }}
                </p>
                <ul
                    class="list-disc space-y-0.5 pl-4 text-xs text-primary-700 dark:text-primary-300"
                >
                    <li
                        v-for="(item, index) in insight.recommendations"
                        :key="index"
                    >
                        {{ item }}
                    </li>
                </ul>
            </div>

            <button
                type="button"
                class="text-xs text-gray-400 underline transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                :disabled="loading"
                @click="generate"
            >
                {{ t("dashboard.aiInsight.regenerate") }}
            </button>
        </div>

        <p v-else class="text-sm text-gray-500 dark:text-gray-400">
            {{ t("dashboard.aiInsight.hint") }}
        </p>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { Loader2, Sparkles, X } from "@lucide/vue";
import aiService from "@/api/ai";
import { runAiTask } from "@/composables/useAiTask";

export interface AiInsight {
    summary: string;
    highlights: string[];
    risks: string[];
    recommendations: string[];
}

const { t } = useI18n();

const insight = ref<AiInsight | null>(null);
const loading = ref(false);
const error = ref<string | null>(null);

const generate = async (): Promise<void> => {
    if (loading.value) return;

    loading.value = true;
    error.value = null;

    try {
        insight.value = await runAiTask<AiInsight>(() =>
            aiService.getDashboardInsight(),
        );
    } catch (err) {
        console.error("AI insight failed:", err);
        error.value =
            (err as { response?: { data?: { message?: string } } })?.response
                ?.data?.message || t("dashboard.aiInsight.failed");
    } finally {
        loading.value = false;
    }
};
</script>
